import type { FileResponse } from '~/clients'

export const downloadFileFromUrl = async (url: string, fileName: string) => {
  try {
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`)
    }

    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = blobUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()

    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('Error downloading the file:', error)
  }
}

export const downloadFile = (
  file: FileResponse,
  fileName: string,
  type: string = 'application/pdf',
) => {
  try {
    const data = new Blob([file.data], { type })
    const downloadUrl = window.URL.createObjectURL(data)
    downloadFileFromUrl(downloadUrl, fileName)
  } catch (error) {
    console.error('Error downloading the file:', error)
  }
}

export const downloadFileUrl = (downloadUrl: string, fileName: string) => {
  const a = document.createElement('a')
  a.href = downloadUrl
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(downloadUrl)
  a.remove()
}

export const downloadFileFromBlob = (blob: Blob, fileName: string) => {
  try {
    const downloadUrl = window.URL.createObjectURL(blob)
    downloadFileUrl(downloadUrl, fileName)
  } catch (error) {
    console.error('Error downloading the file:', error)
  }
}

export type ParsedFile = {
  mimeType: string
  extension: string
  blob?: Blob
  file?: File
}

/**
 * Parses a base64-encoded file string and extracts metadata.
 * Optionally returns a Blob or File object (browser).
 */
export function parseBase64File(
  base64: string,
  opts?: {
    filename?: string
    mimeType?: string
    asBlobOrFile?: boolean
  },
): ParsedFile {
  const { filename, mimeType, asBlobOrFile } = opts || {}

  const signatures: Record<string, { mime: string; prefix: string }> = {
    png: { mime: 'image/png', prefix: 'iVBORw0K' },
    jpg: { mime: 'image/jpeg', prefix: '/9j/' },
    gif: { mime: 'image/gif', prefix: 'R0lGOD' },
    pdf: { mime: 'application/pdf', prefix: 'JVBER' },
    zip: { mime: 'application/zip', prefix: 'UEsD' },
    webp: { mime: 'image/webp', prefix: 'UklGR' },
  }

  const matched = Object.entries(signatures).find(([_, sig]) => base64.startsWith(sig.prefix))

  let ext = matched?.[0]
  let detectedMime = matched?.[1].mime

  if (!ext && filename) {
    const extMatch = filename.match(/\.([a-z0-9]+)$/i)
    if (extMatch) ext = extMatch[1].toLowerCase()
  }

  if (!detectedMime && mimeType && mimeType !== 'application/octet-stream') {
    detectedMime = mimeType
  }

  if (!detectedMime) detectedMime = 'application/octet-stream'
  if (!ext) ext = detectedMime.split('/')[1] || 'bin'

  let blob: Blob | undefined
  let file: File | undefined
  if (asBlobOrFile && typeof window !== 'undefined') {
    const byteChars = atob(base64)
    const byteNums = new Array(byteChars.length)
    for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i)
    const byteArray = new Uint8Array(byteNums)
    blob = new Blob([byteArray], { type: detectedMime })
    if (filename) file = new File([blob], filename, { type: detectedMime })
  }

  return { mimeType: detectedMime, extension: ext, blob, file }
}
